import type { ChangeEvent } from "react";
import { FIXTURES } from "../fixtures";
import type { EngineError } from "../contracts/types";
import type { InputState, RunState } from "../hooks/useEngine";

interface WorkbenchProps {
  scenarioId: string;
  inputText: string;
  inputState: InputState;
  inputErrors: EngineError[];
  runState: RunState;
  uploadError: string | null;
  onSelectScenario: (id: string) => void;
  onEditInput: (text: string) => void;
  onLoadFile: (file: File) => void;
  onRun: () => void;
  onCancel: () => void;
}

export function Workbench({
  scenarioId,
  inputText,
  inputState,
  inputErrors,
  runState,
  uploadError,
  onSelectScenario,
  onEditInput,
  onLoadFile,
  onRun,
  onCancel,
}: WorkbenchProps) {
  const isInvalid = inputState === "invalid" || inputState === "too-large";
  const isProcessing = runState === "processing";

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) onLoadFile(file);
    event.target.value = "";
  }

  return (
    <section id="workbench" aria-labelledby="workbench-heading">
      <h2 id="workbench-heading" className="section-heading">
        Entrada y configuración
      </h2>
      <div className="workbench-grid">
        <div className="panel">
          <div className="field">
            <label htmlFor="scenario-select">Escenario</label>
            <select id="scenario-select" value={scenarioId} onChange={(event) => onSelectScenario(event.target.value)}>
              {FIXTURES.map((fixture) => (
                <option key={fixture.id} value={fixture.id}>
                  {fixture.label}
                </option>
              ))}
            </select>
            <p className="field-hint">{FIXTURES.find((fixture) => fixture.id === scenarioId)?.description}</p>
          </div>

          <div className="field">
            <label htmlFor="file-upload">Cargar archivo local (.json o .csv)</label>
            <input id="file-upload" type="file" accept=".json,.csv,application/json,text/csv" onChange={handleFileChange} />
            <p className="field-hint">
              El archivo se procesa en memoria de tu navegador; nunca se sube a ningún servidor. Límite: 10 MB.
            </p>
            {uploadError ? (
              <p className="field-error" role="alert">
                {uploadError}
              </p>
            ) : null}
          </div>

          <div className="action-row">
            <button type="button" className="button button-primary" onClick={onRun} disabled={isInvalid || isProcessing}>
              {isProcessing ? "Procesando…" : "Ejecutar escenario"}
            </button>
            {isProcessing ? (
              <button type="button" className="button button-danger" onClick={onCancel}>
                Cancelar
              </button>
            ) : null}
          </div>
        </div>

        <div className="panel">
          <div className="field">
            <label htmlFor="input-editor">Entrada (envolvente del motor)</label>
            <textarea
              id="input-editor"
              className="code"
              spellCheck={false}
              value={inputText}
              aria-invalid={isInvalid}
              aria-describedby={isInvalid ? "input-errors" : undefined}
              onChange={(event) => onEditInput(event.target.value)}
            />
            {isInvalid ? (
              <div id="input-errors" className="field-error" role="alert">
                <p>No pudimos procesar esta entrada. Tus datos no se enviaron; corrige los campos señalados.</p>
                <ul>
                  {inputErrors.map((error, index) => (
                    <li key={`${error.code}-${index}`}>
                      {error.path ? <code>{error.path}</code> : null} {error.message}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="field-hint">
                <code>payload.records</code> son las filas del dataset; <code>payload.rules</code> son reglas de
                máscara opcionales por campo. Un campo sin regla se detecta pero se conserva tal cual: puedes
                configurar cada regla aquí o después de ejecutar, en la tabla de campos del resultado.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
